import type { KLineData } from 'klinecharts'

/**
 * Heikin Ashi transform on klinecharts bars. HA close = avg(o,h,l,c); HA open =
 * avg(prev HA open, prev HA close); high/low extend to include the HA body.
 *
 * Note: HA is recursive, so a paginated chunk transformed on its own is seeded
 * from its first raw candle — a tiny discontinuity at chunk boundaries that
 * fades within a few bars. Acceptable for the sketching workbench.
 */
export function toHeikinAshi(bars: KLineData[]): KLineData[] {
  const out: KLineData[] = []
  for (let i = 0; i < bars.length; i++) out.push(nextHeikinAshi(out[i - 1], bars[i]))
  return out
}

/**
 * One Heikin Ashi bar from the HA bar before it — the step `toHeikinAshi` folds over.
 *
 * Exported for the canvas replay, which appends bars (and repaints a forming one) one at a
 * time: re-transforming the whole prefix per bar would be O(n) on every tick. HA is causal, so
 * chaining this off the engine's own last HA bar gives exactly what the full transform would.
 * With no previous bar it seeds from the raw bar, as the full transform does at index 0.
 */
export function nextHeikinAshi(prev: KLineData | null | undefined, b: KLineData): KLineData {
  const close = (b.open + b.high + b.low + b.close) / 4
  const open = prev ? (prev.open + prev.close) / 2 : (b.open + b.close) / 2
  return {
    timestamp: b.timestamp,
    open,
    close,
    high: Math.max(b.high, open, close),
    low: Math.min(b.low, open, close),
    volume: b.volume,
  }
}
